"use client"

import * as React from "react"
import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer"
import { XIcon } from "lucide-react"
import { Button } from "@/primitives/button"
import { useFullScreenTask } from "@/design-system/full-screen-task.ts"
import { useBackDismiss } from "@/design-system/use-back-dismiss.ts"
import { useOverlayOpen } from "@/design-system/use-overlay-form.ts"
import { cn } from "@/primitives/utils"

/*
 * beacon: the surface every sheet is built on, on the compact layout: a Dialog or Popover opened on
 * a phone, the Sheet, the person card on touch. A bottom sheet rises from the screen's edge on the
 * popover's fill and the dialog's shadow, its top corners rounded, with a 36×4 handle that drags it
 * shut; it stays above the on-screen keyboard and clear of the home indicator. A side sheet (the
 * Sheet's) slides in full height. Back closes any of them (`useBackDismiss`).
 */

type DrawerContextProps = {
  modal: DrawerPrimitive.Root.Props["modal"]
  swipeDirection: NonNullable<DrawerPrimitive.Root.Props["swipeDirection"]>
}

const DrawerContext = React.createContext<DrawerContextProps | null>(null)

function useDrawer() {
  const context = React.useContext(DrawerContext)

  if (!context) {
    throw new Error("useDrawer must be used within a Drawer.")
  }

  return context
}

function Drawer({
  modal = true,
  swipeDirection = "down",
  actionsRef,
  ...props
}: DrawerPrimitive.Root.Props) {
  const contextValue = React.useMemo(() => ({ modal, swipeDirection }), [modal, swipeDirection])
  // beacon: Back closes the drawer, through the root's own close, so `onOpenChange` hears it.
  const ownActions = React.useRef<DrawerPrimitive.Root.Actions>(null)
  const actions = actionsRef ?? ownActions
  const { open, onOpenChange } = useOverlayOpen(props)
  useBackDismiss(open, () => actions.current?.close())

  return (
    <DrawerContext.Provider value={contextValue}>
      <DrawerPrimitive.Root
        data-slot="drawer"
        modal={modal}
        swipeDirection={swipeDirection}
        actionsRef={actions}
        {...props}
        onOpenChange={onOpenChange}
      />
    </DrawerContext.Provider>
  )
}

function DrawerTrigger({ ...props }: DrawerPrimitive.Trigger.Props) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
}

function DrawerPortal({ ...props }: DrawerPrimitive.Portal.Props) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
}

function DrawerClose({ ...props }: DrawerPrimitive.Close.Props) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
}

function DrawerOverlay({ className, ...props }: DrawerPrimitive.Backdrop.Props) {
  return (
    <DrawerPrimitive.Backdrop
      data-slot="drawer-overlay"
      className={cn(
        // beacon: the scrim token, with no blur, lightening as the drawer is dragged away, on the
        // sheet's motion (see DrawerContent).
        "fixed inset-0 z-50 min-h-dvh bg-scrim opacity-[calc(1-var(--drawer-swipe-progress,0))] transition-opacity duration-200 ease-snappy select-none data-ending-style:pointer-events-none data-ending-style:opacity-0 data-ending-style:duration-[calc(var(--drawer-swipe-strength)*150ms)] data-starting-style:opacity-0 data-swiping:duration-0 supports-[-webkit-touch-callout:none]:absolute",
        className,
      )}
      {...props}
    />
  )
}

/**
 * beacon: the 36×4 bar in the strong hairline, centred at the top of every bottom sheet, so a
 * Drawer, a docked Menu or Select and a docked alert read as the same object. It is decorative: only
 * a Drawer's drags (`DrawerSwipeHandle`), and a sheet that scrolls keeps it pinned at its top.
 */
function BottomSheetHandle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="bottom-sheet-handle"
      aria-hidden="true"
      className={cn(
        "flex h-5 shrink-0 items-center justify-center after:block after:h-1 after:w-9 after:rounded-full after:bg-border-strong",
        className,
      )}
      {...props}
    />
  )
}

function DrawerSwipeHandle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <BottomSheetHandle
      data-slot="drawer-swipe-handle"
      // beacon: the sheet's handle, which drags it shut.
      className={cn("cursor-grab active:cursor-grabbing", className)}
      {...props}
    />
  )
}

/*
 * beacon: how much of the screen's bottom the on-screen keyboard covers, which a bottom sheet rides
 * above (`--sheet-keyboard`, which the sheet utilities in `tokens.css` read): the drawer's own
 * measure while its keyboard provider is aligned, else the shell's.
 */
const keyboard = "[--sheet-keyboard:var(--drawer-keyboard-inset,var(--keyboard-inset,0px))]"

/*
 * beacon: a bottom sheet's height, above the keyboard (`tokens.css`). `auto` is as tall as its
 * content, up to 2.5rem short of the top of the screen; `full` stands 0.5rem short of it, for a long
 * task (an `xl` dialog, as the lightbox). `screen` is the whole screen, square-cornered on the
 * panel's surface, with no handle and its content clear of the status bar, for a full-screen task
 * (Search, the Composer); the shell's tab bar steps aside while one is open. A sheet opened from a
 * full screen (a Composer's Picker) rises over it, and the screen stands still and whole beneath it,
 * dimmed by the scrim (`ScreenScrim`), rather than shrinking back to the sheet's height as a stacked
 * sheet does.
 */
const drawerSizes = {
  auto: "max-h-sheet",
  full: "h-sheet-full",
  screen:
    "h-sheet-screen bg-background pt-safe shadow-none after:bg-background data-[swipe-axis=y]:max-w-none data-[swipe-direction=down]:rounded-none [--peek:0px] [--stack-height:var(--available-height)] [--stack-step:0]",
} as const

/**
 * beacon: the scrim over a full-screen drawer while a sheet opened from it is up, as the scrim a
 * docked menu rises over (Base UI draws no backdrop for a nested drawer). It lightens as that sheet
 * is dragged away. The nested drawer sets `--drawer-swipe-progress` on this popup, but Base UI
 * registers it as a property that does not inherit, so the scrim reads the popup's own unregistered
 * copy of it, `--stack-progress`, which does.
 */
function ScreenScrim() {
  return (
    <div
      aria-hidden="true"
      data-slot="drawer-screen-scrim"
      className="pointer-events-none absolute inset-0 z-20 bg-scrim opacity-0 transition-opacity duration-200 ease-snappy group-data-nested-drawer-open/drawer-popup:opacity-[calc(1-var(--stack-progress,0))] group-data-nested-drawer-swiping/drawer-popup:duration-0"
    />
  )
}

/*
 * beacon: a full screen opened from a sheet (an EditorSheet from the Ticket plan's sheet) covers
 * it, so the sheet beneath stands still and keeps what it holds, as a full screen does under a sheet
 * opened from it, rather than shrinking back, emptying and jumping to the screen's height as a
 * stacked sheet does: otherwise all that shows while the screen rises and slides away. Each drawer
 * tells the drawer it was opened from through this, by how many screens it adds over it.
 */
const ScreenOverContext = React.createContext<((change: 1 | -1) => void) | null>(null)

/**
 * beacon: says a full-screen drawer is open, for as long as its popup is mounted: to the shell, and
 * to the drawer it was opened from, if any.
 */
function FullScreenTask() {
  useFullScreenTask(true)
  const screenOver = React.useContext(ScreenOverContext)
  React.useLayoutEffect(() => {
    if (!screenOver) return
    screenOver(1)
    return () => screenOver(-1)
  }, [screenOver])
  return null
}

/**
 * beacon: the drawer's surface. `className` styles the surface; what it holds goes in `children`,
 * in a column that scrolls on its own (a footer sticks to its bottom), so a sheet's chrome (the
 * handle, the × with `showCloseButton`) stays put. `size` is a bottom sheet's height.
 */
function DrawerContent({
  className,
  children,
  size = "auto",
  showCloseButton = false,
  ...props
}: DrawerPrimitive.Popup.Props & {
  size?: keyof typeof drawerSizes
  showCloseButton?: boolean
}) {
  const { modal, swipeDirection } = useDrawer()
  const swipeAxis = swipeDirection === "down" || swipeDirection === "up" ? "y" : "x"
  const sheet = swipeDirection === "down"
  // beacon: how many full screens opened from this drawer cover it now (`ScreenOverContext`).
  const [screensOver, setScreensOver] = React.useState(0)
  const countScreenOver = React.useCallback(
    (change: 1 | -1) => setScreensOver((count) => count + change),
    [],
  )
  const screenOver = screensOver > 0

  const surface = (
    <DrawerPortal data-slot="drawer-portal">
      {modal === true && <DrawerOverlay />}
      <DrawerPrimitive.Viewport
        data-slot="drawer-viewport"
        data-modal={modal}
        className="pointer-events-none fixed inset-0 z-50 select-none data-[modal=true]:pointer-events-auto"
      >
        <DrawerPrimitive.Popup
          data-slot="drawer-popup"
          data-swipe-axis={swipeAxis}
          data-size={sheet ? size : undefined}
          // beacon: what an option inside any bottom sheet reads (`in-data-bottom-sheet:`).
          data-bottom-sheet={sheet || undefined}
          data-screen-over={screenOver || undefined}
          className={cn(
            // beacon: the popover's fill and the dialog's shadow, in UI text, with no inset from the
            // screen's edge. The stack is upstream's. The motion is every sheet's, which the docked
            // Menu, Select and AlertDialog share: rising over 200ms on the snappy curve, falling
            // over 150ms, and sooner after a flick (`--drawer-swipe-strength`, 0.1 to 1).
            "group/drawer-popup pointer-events-auto fixed z-50 flex min-h-0 transform-[translate3d(var(--translate-x,0px),var(--translate-y,0px),0)_scale(var(--stack-scale))] flex-col bg-popover text-ui text-popover-foreground shadow-dialog transition-[transform,height,opacity] duration-200 ease-snappy will-change-transform outline-none [--drawer-inset:0px]",
            keyboard,
            // Nested. beacon: upstream also dimmed the drawer under a nested one with a brightness
            // filter, a colour of its own; the nested drawer's scrim dims it here.
            "data-nested-drawer-open:overflow-hidden",
            // Bleed: the fill continues past the edge, so a drag that overshoots shows no gap.
            "after:pointer-events-none after:absolute after:bg-popover data-[swipe-axis=x]:after:inset-y-0 data-[swipe-axis=x]:after:w-(--bleed) data-[swipe-axis=y]:after:inset-x-0 data-[swipe-axis=y]:after:h-(--bleed) data-[swipe-direction=down]:after:top-full data-[swipe-direction=left]:after:right-full data-[swipe-direction=right]:after:left-full data-[swipe-direction=up]:after:bottom-full",
            // Stack.
            "[--bleed:3rem] [--peek:1rem] [--stack-height:var(--drawer-frontmost-height,var(--drawer-height,0px))] [--stack-peek-offset:max(0px,calc((var(--nested-drawers)-var(--stack-progress))*var(--peek)))] [--stack-progress:clamp(0,var(--drawer-swipe-progress),1)] [--stack-scale-base:max(0,calc(1-(var(--nested-drawers)*var(--stack-step))))] [--stack-scale:clamp(0,calc(var(--stack-scale-base)+(var(--stack-step)*var(--stack-progress))),1)] [--stack-shrink:calc(1-var(--stack-scale))] [--stack-step:0.05]",
            // Transitions.
            "data-ending-style:transform-(--closed-transform) data-ending-style:opacity-[0.9999] data-ending-style:duration-[calc(var(--drawer-swipe-strength)*150ms)] data-nested-drawer-swiping:duration-0 data-ending-style:data-nested-drawer-swiping:duration-[calc(var(--drawer-swipe-strength)*150ms)] data-starting-style:transform-(--closed-transform) data-swiping:duration-0 data-ending-style:data-swiping:duration-[calc(var(--drawer-swipe-strength)*150ms)]",
            // beacon: a bottom sheet spans a phone, and stands centred, 36rem wide, on a tablet. It
            // sits on the keyboard, its top corners rounded, and keeps the home indicator's safe
            // area below its content until the keyboard covers it.
            "data-[swipe-axis=y]:inset-x-0 data-[swipe-axis=y]:mx-auto data-[swipe-axis=y]:w-full data-[swipe-axis=y]:max-w-xl",
            !screenOver && "data-[swipe-axis=y]:data-nested-drawer-open:h-(--stack-height)",
            "data-[swipe-direction=down]:bottom-(--sheet-keyboard) data-[swipe-direction=down]:rounded-t-xl data-[swipe-direction=down]:pb-sheet-0",
            // beacon: a side sheet is full height, inside the safe areas, as wide as its caller says.
            "data-[swipe-axis=x]:inset-y-0 data-[swipe-axis=x]:pt-safe data-[swipe-axis=x]:pb-safe",
            // Direction: down.
            "data-[swipe-direction=down]:origin-bottom data-[swipe-direction=down]:[--closed-transform:translate3d(0,calc(100%+var(--drawer-inset,0px)+2px),0)] data-[swipe-direction=down]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)-var(--stack-peek-offset)-(var(--stack-shrink)*var(--stack-height)))]",
            // Direction: up.
            "data-[swipe-direction=up]:top-0 data-[swipe-direction=up]:origin-top data-[swipe-direction=up]:[--closed-transform:translate3d(0,calc(-100%-var(--drawer-inset,0px)-2px),0)] data-[swipe-direction=up]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)+var(--stack-peek-offset)+(var(--stack-shrink)*var(--stack-height)))]",
            // Direction: left.
            "data-[swipe-direction=left]:left-0 data-[swipe-direction=left]:origin-left data-[swipe-direction=left]:pl-safe data-[swipe-direction=left]:[--closed-transform:translate3d(calc(-100%-var(--drawer-inset,0px)-2px),0,0)] data-[swipe-direction=left]:[--translate-x:calc(var(--drawer-swipe-movement-x)+var(--stack-peek-offset)+(var(--stack-shrink)*100%))]",
            // Direction: right.
            "data-[swipe-direction=right]:right-0 data-[swipe-direction=right]:origin-right data-[swipe-direction=right]:pr-safe data-[swipe-direction=right]:[--closed-transform:translate3d(calc(100%+var(--drawer-inset,0px)+2px),0,0)] data-[swipe-direction=right]:[--translate-x:calc(var(--drawer-swipe-movement-x)-var(--stack-peek-offset)-(var(--stack-shrink)*100%))]",
            sheet && drawerSizes[size],
            // beacon: under a full screen opened from it, it stands still, at its own height.
            screenOver && "[--peek:0px] [--stack-step:0]",
            className,
          )}
          {...props}
        >
          {sheet && size !== "screen" && <DrawerSwipeHandle />}
          {sheet && size === "screen" && <FullScreenTask />}
          <DrawerPrimitive.Content
            data-slot="drawer-content"
            className={cn(
              // beacon: the sheet's content scrolls on its own, so a sticky footer stays at its
              // bottom and a drag starts the dismissal only from its top. Under a nested sheet it
              // fades in the overlays' 150ms.
              "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain rounded-[inherit] transition-opacity duration-150 ease-snappy select-text group-data-nested-drawer-open/drawer-popup:opacity-0 group-data-nested-drawer-swiping/drawer-popup:opacity-100 group-data-swiping/drawer-popup:select-none",
              // beacon: a full screen keeps showing what it holds under a sheet opened from it (a
              // Composer's Picker), beneath its scrim; so does a sheet under a full screen opened
              // from it, while the screen rises over it and slides away.
              ((sheet && size === "screen") || screenOver) &&
                "group-data-nested-drawer-open/drawer-popup:opacity-100",
              // beacon: a full screen already stands above the keyboard (`h-sheet-screen`), so the
              // keyboard provider's slack, the padding it gives a scroller it finds reaching under
              // the keyboard, would take the keyboard's height off it a second time, and squeeze
              // what fills it (Search's results) to nothing. Its padding stays its own.
              sheet && size === "screen" && "pb-0!",
            )}
          >
            <ScreenOverContext value={countScreenOver}>{children}</ScreenOverContext>
          </DrawerPrimitive.Content>
          {sheet && size === "screen" && <ScreenScrim />}
          {showCloseButton && (
            <DrawerPrimitive.Close
              data-slot="drawer-close"
              render={
                <Button
                  variant="ghost"
                  className={cn("absolute right-3", sheet ? "top-5" : "top-3")}
                  size="icon-sm"
                />
              }
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DrawerPrimitive.Close>
          )}
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPortal>
  )

  // beacon: a bottom sheet holding a field keeps it in view above the on-screen keyboard.
  return sheet ? (
    <DrawerPrimitive.VirtualKeyboardProvider>{surface}</DrawerPrimitive.VirtualKeyboardProvider>
  ) : (
    surface
  )
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      // beacon: as DialogHeader, under the handle.
      className={cn("flex shrink-0 flex-col gap-1 px-5 pt-1 pb-3", className)}
      {...props}
    />
  )
}

/** beacon: a drawer's content between its header and footer, on the header's gutters. */
function DrawerBody({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="drawer-body" className={cn("px-5 pb-4", className)} {...props} />
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      // beacon: stuck to the sheet's bottom on a hairline, its actions sharing the width. A leading
      // group (a wrapper before them, as Budget's Remove) takes a line of its own above them.
      className={cn(
        "sticky bottom-0 mt-auto flex shrink-0 flex-wrap items-center gap-2 border-t border-border-subtle bg-popover px-5 py-3 *:flex-1 *:[div]:basis-full *:[div]:*:w-full",
        className,
      )}
      {...props}
    />
  )
}

function DrawerTitle({ className, ...props }: DrawerPrimitive.Title.Props) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      // beacon: a title in the type scale.
      className={cn("font-heading text-title font-semibold text-foreground", className)}
      {...props}
    />
  )
}

function DrawerDescription({ className, ...props }: DrawerPrimitive.Description.Props) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      // beacon: UI text in the secondary tier.
      className={cn("text-ui text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  BottomSheetHandle,
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerSwipeHandle,
  DrawerTitle,
  DrawerTrigger,
  useDrawer,
}
